import dayjs from "dayjs";
import { Lockr } from "../Lockr";
import { Language } from "../constants/language";
import { StorageKeys } from "../constants/storageKeys";
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export const dateFromUtc = (
	value: string | null | undefined
): Date | undefined => {
	if (!value) {
		return undefined;
	}

	// Backend timestamps arrive with no zone designator, so drop the fractional seconds and pin
	// them to UTC. A value that already ends in "Z" and has no fractional part becomes
	// "...33Z.000Z", which does not parse, so the fallback reads it as it stands.
	//
	// Known gap: a value carrying a numeric offset AND fractional seconds ("...33.123+02:00")
	// loses the offset to the split and is then read as UTC, so it comes back shifted. The
	// DateTime DTOs this is used on serialise without any designator, so that shape does not
	// occur today; a source that emits DateTimeOffset would need this reworked.
	const pinnedToUtc = new Date(Date.parse(formatTimeSpan(value) + ".000Z"));

	return Number.isNaN(pinnedToUtc.getTime()) ? new Date(Date.parse(value)) : pinnedToUtc;
};

/**
 * UTC ISO-8601 string for an API query, or undefined when the value is missing or resolves to an
 * Invalid Date. Callers must not use `dateFromUtc(...)?.toISOString()`: optional chaining does not
 * catch an Invalid Date, and `toISOString` throws a RangeError on one.
 *
 * This is not input validation. `Date.parse` is lenient about the concatenated form, so a malformed
 * value can still produce a date rather than NaN (`Date.parse("garbage.000Z")` is 2000-01-01), and
 * that date is returned like any other.
 */
export const utcIsoDateOrUndefined = (
	value: string | null | undefined
): string | undefined => {
	const date = dateFromUtc(value);

	return date && !Number.isNaN(date.getTime()) ? date.toISOString() : undefined;
};

const getDateFormatByAppLanguage = (includeYear = false) => {
	const language = Lockr.get(StorageKeys.Language, Language.English);

	if (includeYear) {
		return language === Language.English ? "YYYY/MM/DD" : "DD/MM/YYYY";
	}

	return language === Language.English ? "MM/DD" : "DD/MM";
};

export const dateTimeFormat = (includeYear = false, includeSeconds = true) => {
	if (includeSeconds) {
		return `${getDateFormatByAppLanguage(includeYear)} h:mm:ss A`;
	} else {
		return `${getDateFormatByAppLanguage(includeYear)} h:mm A`;
	}
};
export const dateTimeFormat24H = (includeYear = false) =>
	`${getDateFormatByAppLanguage(includeYear)} HH:mm`;

export const formatDate = (
	date: Date,
	isUtcFormat = true,
	is24HFormat = false,
	includeYear = false,
	includeSeconds = true
) =>
	dayjs(isUtcFormat ? dateFromUtc(date.toString()) : date).format(
		is24HFormat ? dateTimeFormat24H(includeYear) : dateTimeFormat(includeYear, includeSeconds)
	);

export const formatTimeSpan = (timespan: string) =>
	!!timespan ? timespan.split(".")[0] : undefined;

export const formatChatMessageDate = (timestamp: string) => {
	const date = dayjs.utc(timestamp).local();
	const now = dayjs();
  
	const format = date.isBefore(now.subtract(1, 'year')) ? 'MM/DD/YYYY HH:mm' : 'MM/DD HH:mm';
  
	return date.format(format);
  };